import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Loader2,
  GitBranch,
  Shield,
  Check,
  Settings,
  DollarSign,
  List,
} from "lucide-react";
import { CreatePipelineRequest } from "@/types/pipeline";
import { Role } from "@/types/roles";

interface CreatePipelineModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    pipeline: CreatePipelineRequest;
    permissions: {
      role_id: string;
      can_view: boolean;
      can_edit: boolean;
      can_delete: boolean;
    }[];
    stages?: {
      name: string;
      stage_type: string;
      level: number;
      order_position: number;
      color: string;
    }[];
  }) => Promise<void>;
  roles: Role[];
  isLoading?: boolean;
}

interface PermissionFormData {
  role_id: string;
  can_view: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

const DEFAULT_STAGES = [
  {
    name: "Prospectado",
    stage_type: "start",
    level: 0,
    order_position: 1,
    color: "#3B82F6",
  },
  {
    name: "Ganada",
    stage_type: "closed_won",
    level: 2,
    order_position: 2,
    color: "#10B981",
  },
  {
    name: "Perdida",
    stage_type: "closed_lost",
    level: 2,
    order_position: 3,
    color: "#EF4444",
  },
];

export function CreatePipelineModal({
  isOpen,
  onClose,
  onSave,
  roles,
  isLoading = false,
}: CreatePipelineModalProps) {
  const [formData, setFormData] = useState<CreatePipelineRequest>({
    name: "",
    description: "",
    income_objective: undefined,
    is_default: false,
  });

  const [incomeDisplay, setIncomeDisplay] = useState("");
  const [createDefaultStages, setCreateDefaultStages] = useState(true);
  const [permissions, setPermissions] = useState<PermissionFormData[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("general");

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      income_objective: undefined,
      is_default: false,
    });
    setIncomeDisplay("");
    setCreateDefaultStages(true);
    setPermissions([]);
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = "El nombre del pipeline es requerido";
    } else if (formData.name.trim().length < 3) {
      newErrors.name = "El nombre debe tener al menos 3 caracteres";
    }

    if (
      formData.income_objective !== undefined &&
      formData.income_objective < 0
    ) {
      newErrors.income_objective =
        "La meta de ventas debe ser un valor positivo";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave({
        pipeline: formData,
        permissions: permissions,
        stages: createDefaultStages ? DEFAULT_STAGES : undefined,
      });
      onClose();
    } catch (error) {
      console.error("Error al crear pipeline:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    field: keyof CreatePipelineRequest,
    value: string | boolean | number | undefined,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleIncomeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const rawValue = inputValue.replace(/,/g, "");

    if (rawValue === "") {
      setIncomeDisplay("");
      setFormData((prev) => ({ ...prev, income_objective: undefined }));
      if (errors.income_objective) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.income_objective;
          return newErrors;
        });
      }
      return;
    }

    // Permitir solo números y un punto decimal (hasta 2 decimales)
    if (/^\d*\.?\d{0,2}$/.test(rawValue)) {
      const parts = rawValue.split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      const formattedValue = parts.join(".");

      setIncomeDisplay(formattedValue);
      setFormData((prev) => ({
        ...prev,
        income_objective: parseFloat(rawValue),
      }));

      if (errors.income_objective) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.income_objective;
          return newErrors;
        });
      }
    }
  };

  const toggleRolePermission = (roleId: string) => {
    const existing = permissions.find((p) => p.role_id === roleId);
    if (existing) {
      setPermissions(permissions.filter((p) => p.role_id !== roleId));
    } else {
      setPermissions([
        ...permissions,
        { role_id: roleId, can_view: true, can_edit: true, can_delete: false },
      ]);
    }
  };

  const updatePermission = (
    roleId: string,
    field: keyof Omit<PermissionFormData, "role_id">,
    value: boolean,
  ) => {
    setPermissions(
      permissions.map((p) =>
        p.role_id === roleId ? { ...p, [field]: value } : p,
      ),
    );
  };

  const isRoleSelected = (roleId: string) => {
    return permissions.some((p) => p.role_id === roleId);
  };

  const getRolePermission = (roleId: string) => {
    return permissions.find((p) => p.role_id === roleId);
  };

  return (
    <Dialog open={isOpen} onOpenChange={isLoading || isSubmitting ? undefined : onClose}>
      <DialogContent className="w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-hidden p-0 custom-scrollbar rounded-[24px] border-slate-100 flex flex-col">
        <div className="flex flex-col h-full overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full flex flex-col h-full overflow-hidden">
            <div className="bg-white border-b border-slate-100 p-4 sm:px-6 sm:py-4 z-10 relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <DialogHeader>
                <div className="flex items-center space-x-3">
                  <div className="bg-indigo-100/80 p-2.5 rounded-xl">
                    <GitBranch className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div className="text-left">
                    <DialogTitle className="text-lg font-bold text-slate-900 tracking-tight">
                      Crear Nuevo Pipeline
                    </DialogTitle>
                    <DialogDescription className="text-xs text-slate-500 mt-0.5">
                      Configura los detalles básicos y los permisos de acceso para el nuevo pipeline.
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>
              <TabsList className="grid w-full sm:w-auto sm:inline-grid grid-cols-2 bg-slate-100/80 p-1.5 rounded-xl h-10">
                <TabsTrigger value="general" className="rounded-lg px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-indigo-600 data-[state=active]:shadow-sm font-medium transition-all text-sm">Información</TabsTrigger>
                <TabsTrigger value="permissions" className="rounded-lg px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-indigo-600 data-[state=active]:shadow-sm font-medium transition-all text-sm">Permisos</TabsTrigger>
              </TabsList>
            </div>

            <div className="p-6 overflow-y-auto custom-scrollbar flex-1 bg-slate-50/50">
              <div className="space-y-8">
                <TabsContent value="general" className="space-y-6 mt-0 focus:outline-none animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-sm font-medium">
                        Nombre <span className="text-red-500 ml-1">*</span>
                      </Label>
                      <Input
                        id="name"
                        placeholder="Ej: Ventas B2B"
                        value={formData.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        className={errors.name ? "border-red-500" : ""}
                        disabled={isLoading || isSubmitting}
                      />
                      {errors.name && (
                        <p className="text-xs text-red-500 mt-1">{errors.name}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="income_objective"
                        className="text-sm font-medium"
                      >
                        Meta de Ventas
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                          <DollarSign className="h-4 w-4" />
                        </span>
                        <Input
                          id="income_objective"
                          type="text"
                          placeholder="0.00"
                          value={incomeDisplay}
                          onChange={handleIncomeChange}
                          className={`pl-8 ${errors.income_objective ? "border-red-500" : ""}`}
                          disabled={isLoading || isSubmitting}
                        />
                      </div>
                      {errors.income_objective && (
                        <p className="text-xs text-red-500">
                          {errors.income_objective}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Descripción</Label>
                    <Textarea
                      id="description"
                      placeholder="Breve descripción del pipeline..."
                      value={formData.description}
                      onChange={(e) => handleChange("description", e.target.value)}
                      rows={2}
                      disabled={isLoading || isSubmitting}
                      className="resize-none"
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50/50 hover:bg-slate-50 transition-colors">
                    <div className="space-y-0.5">
                      <Label
                        htmlFor="is_default"
                        className="text-base font-medium cursor-pointer"
                      >
                        Pipeline por Defecto
                      </Label>
                      <p className="text-xs text-slate-500">
                        Se seleccionará automáticamente al crear oportunidades
                      </p>
                    </div>
                    <Switch
                      id="is_default"
                      checked={formData.is_default}
                      onCheckedChange={(checked) =>
                        handleChange("is_default", checked)
                      }
                      disabled={isLoading || isSubmitting}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-lg bg-slate-50/50 hover:bg-slate-50 transition-colors">
                    <div className="space-y-0.5">
                      <Label
                        htmlFor="create_stages"
                        className="text-base font-medium cursor-pointer flex items-center gap-2"
                      >
                        <List className="h-4 w-4 text-slate-500" />
                        Crear Etapas Predefinidas
                      </Label>
                      <p className="text-xs text-slate-500">
                        Se crearán automáticamente las etapas estándar (Prospectado, Ganada, Perdida)
                      </p>
                    </div>
                    <Switch
                      id="create_stages"
                      checked={createDefaultStages}
                      onCheckedChange={setCreateDefaultStages}
                      disabled={isLoading || isSubmitting}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="permissions" className="space-y-6 mt-0 focus:outline-none animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <div className="bg-white p-6 rounded-2xl border border-slate-200/60 shadow-sm space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 border-b pb-2 w-full">
                        <Shield className="h-4 w-4 text-slate-500" />
                        <h3 className="text-sm font-medium text-slate-900">
                          Acceso y Permisos
                        </h3>
                      </div>
                    </div>

                    <p className="text-sm text-slate-600 bg-blue-50 p-3 rounded-md border border-blue-100">
                      <span className="font-semibold text-blue-700">Nota:</span> Si no seleccionas ningún rol, el pipeline será público para todos los usuarios.
                    </p>

                    <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
                      <div className="grid grid-cols-12 gap-4 p-3 bg-slate-50/80 border-b text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        <div className="col-span-5 pl-2">Rol</div>
                        <div className="col-span-7 grid grid-cols-3 gap-2 text-center">
                          <span>Ver</span>
                          <span>Editar</span>
                          <span>Eliminar</span>
                        </div>
                      </div>

                      <div className="max-h-[300px] overflow-y-auto">
                        {roles.length === 0 ? (
                          <div className="p-8 text-center text-slate-500 text-sm">
                            No hay roles disponibles.
                          </div>
                        ) : (
                          roles.map((role) => {
                            const isSelected = isRoleSelected(role.id);
                            const permission = getRolePermission(role.id);

                            return (
                              <div
                                key={role.id}
                                className={`grid grid-cols-12 gap-4 p-3 items-center border-b last:border-0 transition-colors ${
                                  isSelected ? "bg-blue-50/40" : "hover:bg-slate-50"
                                }`}
                              >
                                <div
                                  className="col-span-5 flex items-center gap-3 cursor-pointer pl-2"
                                  onClick={() => toggleRolePermission(role.id)}
                                >
                                  <div
                                    className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${
                                      isSelected
                                        ? "bg-blue-600 border-blue-600"
                                        : "border-slate-300 bg-white"
                                    }`}
                                  >
                                    {isSelected && (
                                      <Check className="w-3 h-3 text-white" />
                                    )}
                                  </div>
                                  <span
                                    className={`text-sm font-medium truncate ${
                                      isSelected ? "text-blue-700" : "text-slate-700"
                                    }`}
                                    title={role.name}
                                  >
                                    {role.name}
                                  </span>
                                </div>

                                <div className="col-span-7 grid grid-cols-3 gap-2 justify-items-center">
                                  <Switch
                                    checked={isSelected ? permission?.can_view : false}
                                    onCheckedChange={(checked) =>
                                      isSelected &&
                                      updatePermission(role.id, "can_view", checked)
                                    }
                                    disabled={!isSelected || isLoading || isSubmitting}
                                    className="scale-75"
                                  />
                                  <Switch
                                    checked={isSelected ? permission?.can_edit : false}
                                    onCheckedChange={(checked) =>
                                      isSelected &&
                                      updatePermission(role.id, "can_edit", checked)
                                    }
                                    disabled={!isSelected || isLoading || isSubmitting}
                                    className="scale-75"
                                  />
                                  <Switch
                                    checked={
                                      isSelected ? permission?.can_delete : false
                                    }
                                    onCheckedChange={(checked) =>
                                      isSelected &&
                                      updatePermission(role.id, "can_delete", checked)
                                    }
                                    disabled={!isSelected || isLoading || isSubmitting}
                                    className="scale-75"
                                  />
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </div>
            </div>

            <div className="bg-white border-t border-slate-100 p-4 sm:px-6 sm:py-4 flex-shrink-0">
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={isSubmitting || isLoading}
                  className="w-full sm:w-auto rounded-xl border-slate-200 hover:bg-slate-50 text-slate-700 font-medium transition-all h-11 px-6"
                >
                  Cancelar
                </Button>
                {activeTab === "general" ? (
                  <Button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab("permissions");
                    }}
                    disabled={isSubmitting || isLoading}
                    className="w-full sm:w-auto bg-slate-800 hover:bg-slate-900 text-white rounded-xl shadow-lg font-medium transition-all h-11 px-8"
                  >
                    Siguiente
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      handleSubmit();
                    }}
                    disabled={isSubmitting || isLoading}
                    className="w-full sm:w-auto bg-slate-900 hover:bg-black text-white rounded-xl shadow-lg shadow-slate-900/20 font-medium transition-all h-11 px-8"
                  >
                    {isSubmitting || isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creando...
                      </>
                    ) : (
                      "Crear Pipeline"
                    )}
                  </Button>
                )}
              </div>
            </div>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
